import React, { useCallback, useContext, useMemo, useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { SearchableDropdown } from '@bingads-webui-campaign-react/goals-and-website-tile';
import { withValidation, withError, ValidationGroup } from '@bingads-webui-react/validation';
import { Field, Input } from '@fluentui/react-components';

import {
  businessInfoScema,
  getEmailError,
  getPhoneNumberError,
} from './business-info-helper';

const getStyles = () => ({
  container: {
    width: '100%',
    padding: '10px 0',
    maxWidth: '600px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  'container-m': {
    flexDirection: 'column',
    alignItems: 'center',
  },
  'container-s': {
    flexDirection: 'column',
    alignItems: 'center',
  },
  emailContainer: {
    width: '45%',
    marginRight: '20px',
    '& div[role="alert"]': {
      '& .error': {
        display: 'none',
      },
    },
  },
  'emailContainer-m': {
    width: '100%',
    marginRight: 0,
  },
  'emailContainer-s': {
    width: '100%',
    marginRight: 0,
  },
  phoneContainer: {
    maxWidth: '50%',
  },
  'phoneContainer-m': {
    width: '100%',
    maxWidth: '100%',
  },
  'phoneContainer-s': {
    width: '100%',
    maxWidth: '100%',
  },
  phoneWithCallingCodeContainer: {
    display: 'flex',
    flexDirection: 'row',
    '& .searchable-dropdown-container': {
      width: '110px',
    },
    '& .phone-input': {
      marginLeft: '10px',
      width: '100%',
      display: 'inline-grid',
    },
    '& div[role="alert"]': {
      '& .error': {
        display: 'none',
      },
    },
  },
});

const ValidatedInput = withError(withValidation(props => (<Input {...props} />)));

export function BusinessInfoCollector({
  alwaysShowError,
  email,
  onEmailChange,
  selectedCountryCode,
  countries,
  phoneNumber,
  onPhoneChange,
  setIsValid,
}) {
  const classes = useDefaultStyles(getStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const i18n = useContext(I18nContext);
  const legacyI18n = useContext(LegacyI18nContext);

  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [showEmailError, setShowEmailError] = useState(false);
  const [showPhoneError, setShowPhoneError] = useState(false);

  const callingCodeOptions = useMemo(() => {
    const options = _.map(countries, country => ({
      key: country.Code,
      text: `${country.Code === 'US' ? i18n.getString(_TL_('USA')) : country.Name} (+${country.CountryCallingCode})`,
      callingCode: country.CountryCallingCode,
    }));

    return _.sortBy(options, 'text');
  }, [countries, i18n]);

  const onEmailInputChange = useCallback((e) => {
    onEmailChange(e.target.value);
    setShowEmailError(true);
  }, [onEmailChange]);

  const onCallingCodeChanged = useCallback((e, selection) => {
    onPhoneChange({
      changePhoneNumber: false,
      callingCode: selection.callingCode,
      callingCountryCode: selection.key,
    });
    setShowPhoneError(true);
  }, [onPhoneChange]);

  const onPhoneNumberChange = useCallback((e) => {
    onPhoneChange({
      changePhoneNumber: true,
      phoneNumber: e.target.value,
    });
    setShowPhoneError(true);
  }, [onPhoneChange]);

  const onValidate = useCallback((e) => {
    setIsValid(e.isValid);
  }, [setIsValid]);

  return (
    <div className={classNames('business-info-collector-container', classes.container, classes[`container-${displayMode}`])}>
      <ValidationGroup
        validationProps={{
          schema: businessInfoScema,
          i18n: legacyI18n,
          onValidate,
        }}
      >
        <div className={classNames('email-container', classes.emailContainer, classes[`emailContainer-${displayMode}`])}>
          <Field
            className={classNames('email-input-field', classes.emailInputField)}
            label={i18n.getString(_TL_('Email address'))}
            validationMessage={(alwaysShowError || showEmailError) && emailError}
            required
          >
            <ValidatedInput
              id="email-input"
              value={email}
              onChange={onEmailInputChange}
              placeholder={i18n.getString(_TL_('Enter your email address'))}
              validationProps={{
                field: 'email',
                errorProcessor: getEmailError(i18n, setEmailError),
                alwaysShowError: alwaysShowError || showEmailError,
                debounce: 700,
              }}
            />
          </Field>
        </div>
        <div className={classNames('phone-container', classes.phoneContainer, classes[`phoneContainer-${displayMode}`])}>
          <Field
            className={classNames('phone-input-field', classes.phoneInputField)}
            label={i18n.getString(_TL_('Phone number'))}
            validationMessage={(alwaysShowError || showPhoneError) && phoneError}
            required
          >
            <div className={classNames(
              'phone-with-calling-code-container',
              classes.phoneWithCallingCodeContainer,
              classes[`phoneWithCallingCodeContainer-${displayMode}`]
            )}
            >
              {/* @aiContributed-2025-11-07 */}
              <SearchableDropdown
                aria-label={i18n.getString(_TL_('Select your calling code'))}
                className="calling-code-dropdown"
                selectedKey={selectedCountryCode}
                onChange={onCallingCodeChanged}
                options={callingCodeOptions}
                dropdownWidth="auto"
              />
              <ValidatedInput
                className="phone-input"
                id="phone-input"
                value={phoneNumber}
                onChange={onPhoneNumberChange}
                placeholder={i18n.getString(_TL_('Enter your phone number'))}
                validationProps={{
                  field: 'phone',
                  errorProcessor: getPhoneNumberError(i18n, setPhoneError),
                  alwaysShowError: alwaysShowError || showPhoneError,
                  debounce: 700,
                }}
              />
            </div>
          </Field>
        </div>
      </ValidationGroup>
    </div >
  );
}

BusinessInfoCollector.propTypes = {
  alwaysShowError: PropTypes.bool,
  email: PropTypes.string,
  onEmailChange: PropTypes.func,
  selectedCountryCode: PropTypes.string,
  countries: PropTypes.arrayOf(PropTypes.shape({ Code: PropTypes.string, CountryCallingCode: PropTypes.string })),
  phoneNumber: PropTypes.string,
  onPhoneChange: PropTypes.func,
  setIsValid: PropTypes.func,
};

BusinessInfoCollector.defaultProps = {
  alwaysShowError: false,
  email: '',
  onEmailChange: _.noop,
  selectedCountryCode: '',
  countries: [],
  phoneNumber: '',
  onPhoneChange: _.noop,
  setIsValid: _.noop,
};
